/**
 * Data Enrichment Utilities
 *
 * Utilities for enriching data with reference table information.
 * These utilities handle:
 * - Converting codes to human-readable descriptions
 * - Converting filter values back to codes for backend queries
 * - Looking up values in reference tables
 */

/**
 * Get enriched value from reference table
 *
 * @param {string|number} code - The code to look up
 * @param {Object} table - The reference table with records array
 * @param {string} fallback - Fallback value if not found
 * @returns {string} The enriched description or fallback
 */
export const getEnrichedValue = (code, table, fallback = code) => {
  if (code && table?.records) {
    const lookupRecord = table.records.find(record => record.Code === code)
    if (lookupRecord) {
      const result = lookupRecord.Omschrijving || lookupRecord.Beschrijving
      return result
    }
  }
  return fallback
}

/**
 * Convert enriched filter value back to code
 *
 * When users filter on populated fields, they see human-readable descriptions
 * but the backend expects codes. This function converts the filter value back.
 *
 * @param {string} fieldName - The field name being filtered
 * @param {string} value - The filter value (description)
 * @param {Object} referenceTables - Object mapping field names to their reference tables
 * @returns {string} The code if found, otherwise original value
 */
export const convertEnrichedValueToCode = (fieldName, value, referenceTables) => {
  const table = referenceTables[fieldName]
  if (!table || !table.records || !value) {
    return value // Return original value if no table or no value
  }

  // First try exact match (case-insensitive)
  const normalizedValue = value.toLowerCase().trim()

  let record = table.records.find(r => {
    const description = r.Omschrijving || r.Beschrijving || r.Land || ''
    return description.toLowerCase().trim() === normalizedValue
  })

  // If no exact match, try partial match (contains)
  if (!record) {
    record = table.records.find(r => {
      const description = r.Omschrijving || r.Beschrijving || r.Land || ''
      return description.toLowerCase().includes(normalizedValue)
    })
  }

  // Return the Code if found, otherwise return original value
  return record ? record.Code : value
}

/**
 * Fields that have reference tables and need code conversion for filtering
 */
export const fieldsWithReferenceTables = [
  'station',
  'status',
  'regulationSpecifics.personType',
  'regulationSpecifics.sector',
  'regulationSpecifics.preMissionSector',
  'regulationSpecifics.functionLevel',
  'regulationSpecifics.functionLevelTemporary',
  'regulationSpecifics.functionLevelPartner',
  'regulationSpecifics.jobTitle',
  'regulationSpecifics.preMissionSalaryScale',
  'regulationSpecifics.employer',
  'regulationSpecifics.stationTemporary',
  'regulationSpecifics.rentCurrency',
  'regulationSpecifics.dailyRentCurrency',
  'regulationSpecifics.currencyLocalSalary',
  'regulationSpecifics.country',
  'gender',
  'regulation',
]

/**
 * Check if a field has a reference table
 *
 * @param {string} fieldName - The field name to check
 * @returns {boolean} True if the field has a reference table
 */
export const hasReferenceTable = (fieldName) => {
  return fieldsWithReferenceTables.includes(fieldName)
}
